import React, { useState, useEffect, useRef, ChangeEvent } from "react";
import { Card, Form, Button, InputGroup } from "react-bootstrap";
import { Formik, FormikProps } from "formik";
import { ActionHandlerFunction, UserIdentifier } from "@preveil-api";
import { FormValidationLimits, AccountMessages, AccountErrorMessages, LoginWebUIActionTypes, usePasswordRemoteValidation } from "src/common";
import { Loading, Icon } from "src/components";
import * as yup from "yup";

type AllProps = {
  user_identifier?: UserIdentifier;
  handleAction: ActionHandlerFunction;
}

type FormProps = {
  password: string;
  confirm_password: string;
}

/* Description: Create new password form for PreVeil Express Password Recovery */
function CreateNewPasswordFormComponent(props: AllProps) {
  const { user_identifier, handleAction } = props;
  const [show_password, setShowPassword] = useState<boolean>(false);
  const [show_confirm_password, setShowConfirmPassword] = useState<boolean>(false);
  const [validating, setValidating] = useState<boolean>(false);
  const formik_ref = useRef<FormikProps<FormProps>>(null);
  const password_ref = useRef<HTMLInputElement>(null);
  const { validated, error, validatePassword, resetValidation } = usePasswordRemoteValidation(user_identifier);
  const schema = yup.object().shape({
    password: yup.string()
      .required(AccountErrorMessages.password_required_error)
      .min(
        FormValidationLimits.PASSWORD_MIN_LENGTH,
        AccountErrorMessages.password_helper
      ),
    confirm_password: yup.string()
      .required(AccountErrorMessages.password_required_error)
      .oneOf([yup.ref("password"), null], AccountErrorMessages.password_match_error)
  });

  // Description: Handles the trigger of success or errors from validation completion from hook
  useEffect(() => {
    !!validated && handleSuccess(validated);
    !!error && handleInvalidPassword(error);
  }, [validated, error]);


  // Description: Handle form submit
  function handleSubmit(form_values: FormProps) {
    const params = form_values.password;
    // NOTE: Clear error message from previous attempts
    formik_ref.current?.setStatus(null);
    setValidating(true);
    validatePassword(params);
  }

  // Description: Handle success after remote validation (params is the fully validated password)
  function handleSuccess(params: string) {
    clearFormState();
    handleAction({ actionType: LoginWebUIActionTypes.SubmitNewPassword, params });
  }
  // Description: Handle success after remote validation
  // params = fully validated password
  function handleInvalidPassword(message: string) {
    formik_ref.current?.resetForm();
    formik_ref.current?.setStatus({
      error: message
    });
    password_ref.current?.focus();
    clearFormState();
  }

  // Description: Handle Form reset and clear loading styles
  function handleReset() {
    clearFormState();
    handleAction({ actionType: LoginWebUIActionTypes.ResetLogin });
  }

  // Description: Only clears the form but stays in password step
  function clearFormState() {
    resetValidation();
    setValidating(false);
  }

  return <Card.Body className={`p-3 m-1${validating ? " validating" : ""}`} >
    {
      validating &&
      <Loading message={AccountMessages.validating_password} className="mt-5" />
    }
    <Formik
      innerRef={formik_ref}
      validationSchema={schema}
      onSubmit={handleSubmit}
      initialValues={{
        password: "",
        confirm_password: ""
      }} >
      {({ dirty, values, errors, touched, handleChange, handleSubmit, handleBlur,
        isValid, status, setStatus }) => {
        // NOTE: Wrapper to clear status on any change
        const handleFieldChange = (e: ChangeEvent<any>) => {
          if (status?.error) {
            setStatus(null);
          }
          handleChange(e);
        };
        return <Form noValidate onSubmit={handleSubmit}>

          <p className="fw-bold fs-4">Create a new PreVeil Password</p>
          <p>Please enter your new PreVeil Express password.</p>
          <Form.Group className="mb-3 form-group">
            <Form.Label htmlFor="password_tb" className="fs-6" >Password</Form.Label>
            <InputGroup className={`password-form-control ${!!errors.password && touched.password ? "invalid" : ""}`} hasValidation={true}>
              <Icon className="ficon-lock" />
              <Form.Control
                id="password_tb"
                ref={password_ref}
                type={!!show_password ? "text" : "password"}
                autoFocus={true}
                placeholder="Enter Password"
                name="password"
                value={values.password}
                onBlur={handleBlur}
                onChange={handleFieldChange}
                isInvalid={!!errors.password && touched.password} />
              <Button
                variant="transparent"
                className="btn-icon"
                size="sm"
                tabIndex={-1}
                onClick={() => setShowPassword(!show_password)}
                aria-label="show password" >
                <span>
                  <Icon className={!!show_password ? "ficon-eye" : "ficon-eye-off"} />
                </span>
              </Button>
            </InputGroup>
            {
              !!status?.error ?
                <Form.Control.Feedback type="invalid" className="d-block">
                  {status.error}
                </Form.Control.Feedback> :
                !!errors.password && touched.password ?
                  <Form.Control.Feedback type="invalid" className={`${!!errors.password && touched.password ? "d-block" : ""}`}>
                    {errors.password}
                  </Form.Control.Feedback> :
                  (!status &&
                    <Form.Text className="text-muted d-block" as="div">
                      {AccountErrorMessages.password_helper}
                    </Form.Text>)
            }

          </Form.Group>
          <Form.Group className="form-group">
            <Form.Label htmlFor="confirm_password_tb" className="fs-6" >Confirm Password</Form.Label>
            <InputGroup className={`password-form-control ${!!errors.confirm_password && touched.confirm_password ? "invalid" : ""}`} hasValidation={true}>
              <Icon className="ficon-lock" />
              <Form.Control
                id="confirm_password_tb"
                type={!!show_confirm_password ? "text" : "password"}
                placeholder="Confirm Password"
                name="confirm_password"
                value={values.confirm_password}
                onBlur={handleBlur}
                onChange={handleChange}
                isInvalid={!!errors.confirm_password && touched.confirm_password} />
              <Button
                variant="transparent"
                className="btn-icon"
                size="sm"
                tabIndex={-1}
                onClick={() => setShowConfirmPassword(!show_confirm_password)}
                aria-label="show confirm password">
                <span>
                  <Icon className={!!show_confirm_password ? "ficon-eye" : "ficon-eye-off"} />
                </span>
              </Button>
            </InputGroup>
            <Form.Control.Feedback type="invalid" className={`${!!errors.confirm_password && touched.confirm_password ? "d-block" : ""}`}>
              {errors.confirm_password}
            </Form.Control.Feedback>
          </Form.Group>
          <div className="btn-panel mt-4">
            <Button type="submit" disabled={validating || !dirty || !isValid} >Continue</Button>
            <Button variant="outline-secondary" type="button"
              onClick={handleReset}>Cancel</Button>
          </div>
        </Form>;
      }}
    </Formik>
  </Card.Body>;
}
export default React.memo(CreateNewPasswordFormComponent);
